<template>
  <div class="modal" tabindex="-1" role="dialog" v-show="isVisible">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">年間作業予定の変更登録</h5>
          <button type="button" class="btn-close" @click="closeModal" :disabled="isSubmitting"></button>
        </div>

        <form @submit.prevent="registScheduleDetailChangeInfo">
          <div class="modal-body">
            <div class="mb-3">
              <p class="mb-1"><strong>変更対象年月：</strong>{{ targetWorkYear }}年 {{ targetWorkMonth }}月</p>
              <p class="mb-1"><strong>作業内容：</strong>{{ workScheduleDetail.t_building_work_annual_schedule?.work_content || '' }}</p>
              <p class="mb-1"><strong>請求金額：</strong>{{ formatCurrency(workScheduleDetail.bill_amount) }}</p>
              <p v-if="isShiftedDisplay" class="mb-0 text-muted">
                ※当初予定は {{ workScheduleDetail.work_year }}年 {{ workScheduleDetail.work_month }}月 です。
              </p>
            </div>

            <div v-if="isCurrentCancelChange" class="alert alert-warning py-2 mb-3">
              <div class="fw-bold">この作業予定は中止済みです。</div>
              <div v-if="workScheduleDetailChangeInfo?.reason" class="small mt-1">
                中止理由：{{ workScheduleDetailChangeInfo.reason }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">対応種別 <span class="badge text-bg-danger">必須</span></label>
              <div>
                <label class="form-check form-check-inline">
                  <input class="form-check-input" type="radio" value="reschedule" v-model="actionType" :disabled="isSubmitting" />
                  予定変更
                </label>
                <label class="form-check form-check-inline">
                  <input class="form-check-input" type="radio" value="cancel" v-model="actionType" :disabled="isSubmitting" />
                  中止
                </label>
                <label v-if="isCurrentCancelChange" class="form-check form-check-inline">
                  <input class="form-check-input" type="radio" value="cancel_reversal" v-model="actionType" :disabled="isSubmitting" />
                  中止取消
                </label>
              </div>
            </div>

            <div v-if="actionType === 'reschedule'" class="mb-3">
              <label for="newWorkYear" class="form-label">変更後の作業年月 <span class="badge text-bg-danger">必須</span></label>
              <div class="d-flex">
                <div class="d-flex align-items-center me-2">
                  <input
                    type="number"
                    id="newWorkYear"
                    class="form-control me-1"
                    v-model.number="workScheduleDetailData.changed_work_year"
                    :min="currentYearMonth.year"
                    max="2100"
                    required
                    :disabled="isSubmitting"
                  />
                  <span>年</span>
                </div>
                <div class="d-flex align-items-center">
                  <input
                    type="number"
                    id="newWorkMonth"
                    class="form-control me-1"
                    v-model.number="workScheduleDetailData.changed_work_month"
                    min="1"
                    max="12"
                    required
                    :disabled="isSubmitting"
                  />
                  <span>月</span>
                </div>
              </div>
              <p class="text-muted mb-0 mt-2">
                {{ currentYearMonth.year }}年{{ currentYearMonth.month }}月以降を指定してください。
              </p>
            </div>

            <div v-if="requiresReason" class="mb-2">
              <label for="changeReason" class="form-label">理由 <span class="badge text-bg-danger">必須</span></label>
              <textarea
                id="changeReason"
                class="form-control"
                v-model="workScheduleDetailData.reason"
                rows="3"
                required
                :disabled="isSubmitting"
              ></textarea>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal" :disabled="isSubmitting">キャンセル</button>
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              {{ submitButtonLabel }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="isConfirmVisible" class="confirm-overlay">
      <div class="confirm-card">
        <div class="confirm-header" :class="{ 'confirm-header-warning': confirmDialogType === 'overwrite' }">
          {{ confirmDialogTitle }}
        </div>
        <div class="confirm-body">
          <p
            v-for="(line, index) in confirmMessageLines"
            :key="`confirm-line-${index}`"
            class="mb-1"
          >
            {{ line }}
          </p>
        </div>
        <div class="confirm-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeConfirmDialog"
            :disabled="isSubmitting"
          >
            キャンセル
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="submitScheduleDetailChangeInfo"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? '登録中...' : 'OK' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { getAnnualWorkDetailAndChangeInfo, fixScheduleDetailChangeInfo } from '@/api/axios';

const CHANGE_TYPE_RESCHEDULE = 'reschedule';
const CHANGE_TYPE_CANCEL = 'cancel';
const CHANGE_TYPE_CANCEL_REVERSAL = 'cancel_reversal';

export default {
  emits: ['onClose'],
  setup(_, { emit }) {
    const store = useStore();
    const userRole = computed(() => store.state.userRole ?? localStorage.getItem('userRole'));
    const isAdmin = computed(() => ['0', '999', 0, 999].includes(userRole.value));
    const isVisible = ref(false);
    const isSubmitting = ref(false);
    const isConfirmVisible = ref(false);
    const confirmMessage = ref('');
    const confirmDialogType = ref('normal');
    const pendingPayload = ref(null);
    const currentScheduleDetails = ref([]);
    const workScheduleDetail = ref({});
    const workScheduleDetailChangeInfo = ref(null);
    const currentChangeReason = ref('');

    const targetWorkYear = ref(null);
    const targetWorkMonth = ref(null);
    const currentYearMonth = computed(() => {
      const today = new Date();
      return {
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        value: today.getFullYear() * 100 + (today.getMonth() + 1)
      };
    });

    const workScheduleDetailData = reactive({
      id: null,
      t_building_work_annual_schedule_id: null,
      t_building_work_schedule_detail_id: null,
      original_building_work_schedule_detail_id: null,
      prev_building_work_schedule_detail_change_info_id: null,
      m_building_id: null,
      m_vendor_id: null,
      user_id: null,
      work_year: null,
      work_month: null,
      target_work_year: null,
      target_work_month: null,
      changed_work_year: null,
      changed_work_month: null,
      bill_amount: null,
      status: null,
      reason: '',
      change_type: CHANGE_TYPE_RESCHEDULE,
    });

    const actionType = ref(CHANGE_TYPE_RESCHEDULE);

    const resetWorkScheduleDetailData = () => {
      Object.assign(workScheduleDetailData, {
        id: null,
        t_building_work_annual_schedule_id: null,
        t_building_work_schedule_detail_id: null,
        original_building_work_schedule_detail_id: null,
        prev_building_work_schedule_detail_change_info_id: null,
        m_building_id: null,
        m_vendor_id: null,
        user_id: null,
        work_year: null,
        work_month: null,
        target_work_year: null,
        target_work_month: null,
        changed_work_year: null,
        changed_work_month: null,
        bill_amount: null,
        status: null,
        reason: '',
        change_type: CHANGE_TYPE_RESCHEDULE,
      });
      actionType.value = CHANGE_TYPE_RESCHEDULE;
    };

    const show = ({ workScheduleDetailId, targetWorkYear: y, targetWorkMonth: m, scheduleDetails }) => {
      resetWorkScheduleDetailData();
      isConfirmVisible.value = false;
      confirmMessage.value = '';
      confirmDialogType.value = 'normal';
      pendingPayload.value = null;
      currentScheduleDetails.value = Array.isArray(scheduleDetails) ? scheduleDetails : [];
      currentChangeReason.value = '';

      targetWorkYear.value = y;
      targetWorkMonth.value = m;
      isVisible.value = true;

      fetchScheduleDetailData(workScheduleDetailId, y, m);
    };

    const closeModal = () => {
      if (isSubmitting.value) return;
      isConfirmVisible.value = false;
      confirmMessage.value = '';
      confirmDialogType.value = 'normal';
      pendingPayload.value = null;
      currentScheduleDetails.value = [];
      currentChangeReason.value = '';
      isVisible.value = false;
    };

    const normalizeActionType = (changeInfo) => {
      const type = changeInfo?.change_type;

      if (type === CHANGE_TYPE_CANCEL || Number(type) === 2 || Number(changeInfo?.status) === 3) {
        return CHANGE_TYPE_CANCEL;
      }

      if (type === CHANGE_TYPE_CANCEL_REVERSAL || Number(type) === 3) {
        return CHANGE_TYPE_CANCEL_REVERSAL;
      }

      return CHANGE_TYPE_RESCHEDULE;
    };

    const fetchScheduleDetailData = async (workScheduleDetailId, targetYear, targetMonth) => {
      try {
        const response = await getAnnualWorkDetailAndChangeInfo(workScheduleDetailId, targetYear, targetMonth);
        const { work_detail, work_detail_change_info } = response.data;

        workScheduleDetail.value = work_detail || {};
        workScheduleDetailChangeInfo.value = work_detail_change_info || null;
        currentChangeReason.value = work_detail_change_info?.reason || '';

        if (work_detail_change_info) {
          const normalizedActionType = normalizeActionType(work_detail_change_info);
          actionType.value = normalizedActionType;

          Object.assign(workScheduleDetailData, {
            id: work_detail_change_info.id,
            t_building_work_annual_schedule_id: work_detail_change_info.t_building_work_annual_schedule_id,
            t_building_work_schedule_detail_id: work_detail_change_info.t_building_work_schedule_detail_id,
            original_building_work_schedule_detail_id: work_detail_change_info.original_building_work_schedule_detail_id,
            prev_building_work_schedule_detail_change_info_id: work_detail_change_info.prev_building_work_schedule_detail_change_info_id,
            m_building_id: work_detail_change_info.m_building_id,
            m_vendor_id: work_detail_change_info.m_vendor_id,
            user_id: work_detail_change_info.user_id,
            work_year: work_detail_change_info.target_work_year || targetYear,
            work_month: work_detail_change_info.target_work_month || targetMonth,
            target_work_year: work_detail_change_info.target_work_year || targetYear,
            target_work_month: work_detail_change_info.target_work_month || targetMonth,
            changed_work_year: work_detail_change_info.changed_work_year,
            changed_work_month: work_detail_change_info.changed_work_month,
            bill_amount: work_detail.bill_amount,
            status: work_detail_change_info.status,
            reason: work_detail_change_info.reason || '',
            change_type: normalizedActionType,
          });
        } else {
          actionType.value = CHANGE_TYPE_RESCHEDULE;

          const defaultChangedYearMonth = buildDefaultChangedYearMonth(targetYear, targetMonth);

          Object.assign(workScheduleDetailData, {
            id: null,
            t_building_work_annual_schedule_id: work_detail.t_building_work_annual_schedule_id,
            t_building_work_schedule_detail_id: work_detail.id,
            original_building_work_schedule_detail_id: work_detail.id,
            prev_building_work_schedule_detail_change_info_id: null,
            m_building_id: work_detail.m_building_id,
            m_vendor_id: work_detail.m_vendor_id,
            user_id: work_detail.user_id,
            work_year: targetYear,
            work_month: targetMonth,
            target_work_year: targetYear,
            target_work_month: targetMonth,
            changed_work_year: defaultChangedYearMonth.year,
            changed_work_month: defaultChangedYearMonth.month,
            bill_amount: work_detail.bill_amount,
            status: work_detail.status,
            reason: '',
            change_type: CHANGE_TYPE_RESCHEDULE,
          });
        }
      } catch (error) {
        console.error('作業予定詳細データの取得に失敗しました:', error);
        store.dispatch('setFlashMessage', { message: '作業予定詳細の取得に失敗しました', type: 'danger' });
      }
    };

    const isCurrentCancelChange = computed(() => {
      return normalizeActionType(workScheduleDetailChangeInfo.value) === CHANGE_TYPE_CANCEL;
    });

    const requiresReason = computed(() => {
      return actionType.value !== CHANGE_TYPE_CANCEL_REVERSAL;
    });

    const submitButtonLabel = computed(() => {
      if (isSubmitting.value) return actionType.value === CHANGE_TYPE_CANCEL_REVERSAL ? '取消中...' : '登録中...';
      return actionType.value === CHANGE_TYPE_CANCEL_REVERSAL ? '取消登録' : '登録';
    });

    watch(actionType, (newVal, oldVal) => {
      workScheduleDetailData.change_type = newVal;

      if (newVal === CHANGE_TYPE_CANCEL || newVal === CHANGE_TYPE_CANCEL_REVERSAL) {
        workScheduleDetailData.changed_work_year = null;
        workScheduleDetailData.changed_work_month = null;
      } else if (!workScheduleDetailData.changed_work_year || !workScheduleDetailData.changed_work_month) {
        const defaultChangedYearMonth = buildDefaultChangedYearMonth(targetWorkYear.value, targetWorkMonth.value);
        workScheduleDetailData.changed_work_year = defaultChangedYearMonth.year;
        workScheduleDetailData.changed_work_month = defaultChangedYearMonth.month;
      }

      if (newVal === CHANGE_TYPE_CANCEL_REVERSAL && oldVal !== CHANGE_TYPE_CANCEL_REVERSAL) {
        workScheduleDetailData.reason = '';
      } else if (oldVal === CHANGE_TYPE_CANCEL_REVERSAL && currentChangeReason.value) {
        workScheduleDetailData.reason = currentChangeReason.value;
      }
    });

    const buildConfirmMessage = () => {
      if (actionType.value === CHANGE_TYPE_CANCEL) {
        return '対象の作業予定を中止します。\nこの内容で登録してよろしいでしょうか？';
      }

      if (actionType.value === CHANGE_TYPE_CANCEL_REVERSAL) {
        return '中止を取り消して元の作業予定に戻します。\nこの内容で登録してよろしいでしょうか？';
      }

      return `作業予定を ${workScheduleDetailData.changed_work_year}年${workScheduleDetailData.changed_work_month}月 に変更します。\n予定を変更しますがよろしいでしょうか？`;
    };

    const isBeforeCurrentYearMonth = (year, month) => {
      if (!year || !month) return false;

      return year * 100 + month < currentYearMonth.value.value;
    };

    const buildDefaultChangedYearMonth = (year, month) => {
      if (isBeforeCurrentYearMonth(year, month)) {
        return {
          year: currentYearMonth.value.year,
          month: currentYearMonth.value.month
        };
      }

      return { year, month };
    };

    const validateForm = () => {
      if (!actionType.value) {
        alert('対応種別を選択してください。');
        return false;
      }

      const reason = (workScheduleDetailData.reason || '').trim();
      if (requiresReason.value && !reason) {
        alert('理由を入力してください。');
        return false;
      }

      if (actionType.value === CHANGE_TYPE_RESCHEDULE) {
        if (!workScheduleDetailData.changed_work_year || !workScheduleDetailData.changed_work_month) {
          alert('変更後の作業年月を入力してください。');
          return false;
        }

        if (workScheduleDetailData.changed_work_month < 1 || workScheduleDetailData.changed_work_month > 12) {
          alert('変更後の作業月は1〜12で入力してください。');
          return false;
        }

        if (isBeforeCurrentYearMonth(workScheduleDetailData.changed_work_year, workScheduleDetailData.changed_work_month)) {
          alert(`変更後の作業年月は${currentYearMonth.value.year}年${currentYearMonth.value.month}月以降を指定してください。`);
          return false;
        }

        const overwriteConflictDetail = findOverwriteConflictDetail({
          ...workScheduleDetailData,
          change_type: actionType.value
        });

        if (!isAdmin.value && overwriteConflictDetail?.locked_for_general_user) {
          alert(
            overwriteConflictDetail.has_billing
              ? '変更先の月は請求済みのため編集できません。'
              : '変更先の月は先月より前のため編集できません。'
          );
          return false;
        }
      }

      if (actionType.value === CHANGE_TYPE_CANCEL_REVERSAL && !isCurrentCancelChange.value) {
        alert('中止済みの作業予定ではありません。');
        return false;
      }

      return true;
    };

    const registScheduleDetailChangeInfo = async () => {
      if (!validateForm()) return;

      const isCancelReversal = actionType.value === CHANGE_TYPE_CANCEL_REVERSAL;

      pendingPayload.value = {
        ...workScheduleDetailData,
        id: isCancelReversal ? null : workScheduleDetailData.id,
        prev_building_work_schedule_detail_change_info_id: isCancelReversal
          ? workScheduleDetailChangeInfo.value?.id
          : workScheduleDetailData.prev_building_work_schedule_detail_change_info_id,
        reason: requiresReason.value ? workScheduleDetailData.reason.trim() : null,
        target_work_year: targetWorkYear.value,
        target_work_month: targetWorkMonth.value,
        work_year: targetWorkYear.value,
        work_month: targetWorkMonth.value,
        change_type: actionType.value,
        apply_to_future: false,
        changed_work_year: actionType.value === CHANGE_TYPE_RESCHEDULE ? workScheduleDetailData.changed_work_year : null,
        changed_work_month: actionType.value === CHANGE_TYPE_RESCHEDULE ? workScheduleDetailData.changed_work_month : null,
      };

      const overwriteConflictDetail = findOverwriteConflictDetail(pendingPayload.value);
      if (overwriteConflictDetail) {
        confirmDialogType.value = 'overwrite';
        confirmMessage.value = buildOverwriteWarningMessage(pendingPayload.value);
      } else {
        confirmDialogType.value = 'normal';
        confirmMessage.value = buildConfirmMessage();
      }

      isConfirmVisible.value = true;
    };

    const closeConfirmDialog = () => {
      if (isSubmitting.value) return;
      isConfirmVisible.value = false;
    };

    const submitScheduleDetailChangeInfo = async () => {
      if (!pendingPayload.value) return;
      isSubmitting.value = true;

      try {
        await fixScheduleDetailChangeInfo(pendingPayload.value);
        store.dispatch('setFlashMessage', {
          message: pendingPayload.value.change_type === CHANGE_TYPE_CANCEL_REVERSAL ? '中止を取り消しました' : '予定修正を登録しました',
          type: 'success'
        });

        isConfirmVisible.value = false;
        confirmMessage.value = '';
        confirmDialogType.value = 'normal';
        pendingPayload.value = null;
        currentScheduleDetails.value = [];
        currentChangeReason.value = '';
        isVisible.value = false;
        emit('onClose');
      } catch (error) {
        console.error('予定修正エラー:', error);
        const message = error?.response?.data?.error || '予定修正の処理に失敗しました';
        store.dispatch('setFlashMessage', { message, type: 'danger' });
      } finally {
        isSubmitting.value = false;
      }
    };

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
      }).format(value || 0);
    };

    const isShiftedDisplay = computed(() => {
      const originalYear = Number(workScheduleDetail.value?.work_year);
      const originalMonth = Number(workScheduleDetail.value?.work_month);
      const targetYear = Number(targetWorkYear.value);
      const targetMonth = Number(targetWorkMonth.value);

      if (!originalYear || !originalMonth || !targetYear || !targetMonth) return false;

      return originalYear !== targetYear || originalMonth !== targetMonth;
    });

    const confirmMessageLines = computed(() => {
      return (confirmMessage.value || '')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
    });

    const confirmDialogTitle = computed(() => {
      if (actionType.value === CHANGE_TYPE_CANCEL_REVERSAL) return '中止取消確認';
      return confirmDialogType.value === 'overwrite' ? '上書き確認' : '予定変更確認';
    });

    const buildOverwriteWarningMessage = (payload) => {
      return `変更先の ${payload.changed_work_year}年${payload.changed_work_month}月 には、すでに予定が入っています。\nこの月の予定を上書きしてよろしいですか？`;
    };

    const isActivePlannedDetail = (detail) => {
      if (!detail?.is_planned) return false;
      if (detail.change_display_type === 'shifted_source') return false;
      if (detail.change_display_type === 'cancel') return false;
      return true;
    };

    const findOverwriteConflictDetail = (payload) => {
      if (payload?.change_type !== CHANGE_TYPE_RESCHEDULE) return null;
      if (!payload.changed_work_year || !payload.changed_work_month) return null;

      const targetYear = Number(payload.changed_work_year);
      const targetMonth = Number(payload.changed_work_month);
      const sourceDetailId = Number(payload.t_building_work_schedule_detail_id);

      return currentScheduleDetails.value.find((detail) => {
        if (!detail) return false;
        if (Number(detail.work_year) !== targetYear || Number(detail.work_month) !== targetMonth) return false;
        if (!isActivePlannedDetail(detail)) return false;

        const detailSourceId = Number(detail.source_detail_id || detail.id);
        return detailSourceId !== sourceDetailId;
      }) || null;
    };

    return {
      isVisible,
      isSubmitting,
      isConfirmVisible,
      confirmDialogType,
      actionType,
      targetWorkYear,
      targetWorkMonth,
      currentYearMonth,
      workScheduleDetail,
      workScheduleDetailChangeInfo,
      workScheduleDetailData,
      isShiftedDisplay,
      isCurrentCancelChange,
      requiresReason,
      submitButtonLabel,
      confirmMessageLines,
      confirmDialogTitle,
      show,
      closeModal,
      closeConfirmDialog,
      registScheduleDetailChangeInfo,
      submitScheduleDetailChangeInfo,
      formatCurrency,
    };
  },
};
</script>

<style scoped>
.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  max-width: 560px;
}

.confirm-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1060;
  background-color: rgba(0, 0, 0, 0.5);
  overflow-x: hidden;
  overflow-y: auto;
}

.confirm-card {
  width: calc(100% - 1rem);
  max-width: 520px;
  margin: 1.75rem auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.confirm-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e5e5;
  font-weight: bold;
}

.confirm-header-warning {
  color: #b23c17;
}

.confirm-body {
  padding: 1rem;
  max-height: 240px;
  overflow-y: auto;
}

.confirm-footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
